import { QueryParams } from '../../../types/baseExplorerConfig';

/**
 * Translation of explorer query params into lightning service params.
 *
 * Kept in its own module so the rules can be tested against the real code
 * rather than a copy: the test previously reimplemented this logic inline to
 * avoid importing `lightningService`, and the copy drifted (#836).
 */

const NUMERIC_FIELD_MAP: Record<string, { min: string; max: string }> = {
  peakCurrent: { min: 'minCurrent', max: 'maxCurrent' },
  lat: { min: 'minLat', max: 'maxLat' },
  lon: { min: 'minLon', max: 'maxLon' },
  multiplicity: { min: 'minMultiplicity', max: 'maxMultiplicity' },
  originatingSensors: { min: 'minSensors', max: 'maxSensors' },
  angleIndicator: { min: 'minAngle', max: 'maxAngle' },
  signalIndicator: { min: 'minSignal', max: 'maxSignal' },
};

const NWT_TZ = 'America/Yellowknife';

/** The UTC offset in effect in the NWT at an instant, as "+HH:MM"/"-HH:MM". */
function nwtOffsetAt(instant: number): string {
  const name = new Intl.DateTimeFormat('en-CA', { timeZone: NWT_TZ, timeZoneName: 'longOffset' })
    .formatToParts(new Date(instant))
    .find(p => p.type === 'timeZoneName')!.value;
  return name.replace('GMT', '') || '+00:00';
}

/**
 * An NWT wall-clock moment on a picked day, as an ISO instant (#836).
 *
 * A picked date is an NWT operational day whatever zone the viewer's machine
 * is set to. `new Date('YYYY-MM-DDTHH:mm:ss')` resolves in the viewer's zone,
 * so the filtered window used to follow the operator's laptop rather than the
 * territory — the same shape as #801.
 *
 * The offset is resolved twice: the first pass uses the offset at UTC midnight
 * to place the moment, the second confirms it against the offset actually in
 * effect there, which differ on a day that changes offset.
 */
function nwtInstant(day: string, wallClock: string): string {
  const firstPass = Date.parse(`${day}T${wallClock}${nwtOffsetAt(Date.parse(`${day}T00:00:00Z`))}`);
  return new Date(Date.parse(`${day}T${wallClock}${nwtOffsetAt(firstPass)}`)).toISOString();
}

// Used by both fetchData (view) and buildExportParams (export) — same translation, no drift.
export function translateParams(params: QueryParams): Record<string, any> {
  const out: Record<string, any> = {};

  if (params?.search?.term) {
    out.search = params.search.term;
  }

  if (params?.sort?.[0]) {
    out.sortBy = params.sort[0].field;
    out.sortOrder = params.sort[0].direction;
  }

  if (!params?.filters) return out;

  for (const filter of params.filters) {
    if (filter.field === 'strikeDate') {
      if (filter.operator === 'gte' && filter.value) {
        out.startDate = nwtInstant(filter.value, '00:00:00.000');
      } else if (filter.operator === 'lte' && filter.value) {
        out.endDate = nwtInstant(filter.value, '23:59:59.999');
      }
      continue;
    }

    if (filter.field === 'strikeCharge') {
      out.chargeType = filter.value;
      continue;
    }

    if (filter.field === 'type') {
      out.strikeType = filter.value;
      continue;
    }

    if (filter.field === 'cloudIndicator') {
      out.cloudIndicator = filter.value;
      continue;
    }

    const mapping = NUMERIC_FIELD_MAP[filter.field];
    if (mapping) {
      const nv = filter.value as { operator: string; value?: number; value2?: number };
      if (!nv?.operator) continue;
      if (nv.operator === 'between') {
        if (nv.value != null) out[mapping.min] = nv.value;
        if (nv.value2 != null) out[mapping.max] = nv.value2;
      } else if (nv.operator === 'gt') {
        if (nv.value != null) { out[mapping.min] = nv.value; out[mapping.min + 'Strict'] = '1'; }
      } else if (nv.operator === 'gte') {
        if (nv.value != null) out[mapping.min] = nv.value;
      } else if (nv.operator === 'lt') {
        if (nv.value != null) { out[mapping.max] = nv.value; out[mapping.max + 'Strict'] = '1'; }
      } else if (nv.operator === 'lte') {
        if (nv.value != null) out[mapping.max] = nv.value;
      } else if (nv.operator === 'eq' && nv.value != null) {
        out[mapping.min] = nv.value;
        out[mapping.max] = nv.value;
      }
      continue;
    }

    console.warn(`Lightning ServiceAdapter: Unsupported filter field: ${filter.field}`);
  }

  return out;
}

